import { useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { ChevronRight, Menu, X } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ShareLinkButton } from "@/features/explorer/components/ShareLinkButton";
import { cn } from "@/lib/utils";

const navItems = [
  { label: "Map Explorer", to: "/" as const },
  { label: "Leaderboard", to: "/leaderboard" as const },
  { label: "Charts", to: "/charts" as const },
  { label: "About", to: "/about" as const },
  { label: "Contact", to: "/contact" as const },
];

const projectName = "MetricMap";

type ExplorerHeaderProps = {
  isNavExpanded?: boolean;
};

export function ExplorerHeader({ isNavExpanded = false }: ExplorerHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (item: (typeof navItems)[number]) => {
    return location.pathname === item.to;
  };

  return (
    <header className='pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center px-0'>
      <div
        id='tour-header-nav'
        className='pointer-events-auto relative flex w-full items-center justify-between border-b border-border bg-card/95 px-3 py-2.5 sm:py-3 text-card-foreground shadow-sm backdrop-blur-2xl sm:px-4 lg:px-6'>
        <div className='flex items-center gap-3'>
          {/* Desktop Unified Brand Badge with Inline Text & Integrated Flyout Nav */}
          <div className='group hidden items-center lg:flex'>
            <div
              id='tour-export-nav'
              className='flex items-center rounded-lg border border-border bg-muted/60 p-1.5 shadow-xs transition-[border-color,background-color] duration-300 hover:border-primary/50 hover:bg-muted'>
              <Link
                className='flex items-center gap-2.5 rounded-md px-2 py-1 transition-colors hover:bg-muted'
                to='/'
                aria-label='MetricMap home'>
                <img src='/uk-flag.svg' alt='' className='size-6 rounded-xs object-cover shadow-xs' />
                <div className='flex items-center gap-2'>
                  <span className='text-sm font-extrabold tracking-wider text-foreground uppercase'>{projectName}</span>
                  <span className='rounded-xs border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary uppercase tracking-wider'>
                    UK Metrics
                  </span>
                </div>
              </Link>

              <div
                className={cn(
                  "mx-1.5 flex items-center justify-center text-muted-foreground transition-colors group-hover:text-primary",
                  isNavExpanded && "text-primary",
                )}>
                <ChevronRight
                  className={cn(
                    "size-4 transition-transform duration-300 group-hover:translate-x-0.5",
                    isNavExpanded && "translate-x-0.5",
                  )}
                />
              </div>

              <nav
                className={cn(
                  "flex items-center gap-1 overflow-hidden transition-[max-width,padding,opacity] duration-300 ease-in-out group-hover:max-w-md group-hover:px-1 group-hover:opacity-100",
                  isNavExpanded ? "max-w-md px-1 opacity-100" : "max-w-0 opacity-0",
                )}>
                {navItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={() => {
                      setIsMenuOpen(false);
                    }}
                    className={cn(
                      "rounded-md px-2.5 py-1 text-xs font-semibold whitespace-nowrap transition-colors",
                      isActive(item)
                        ? "bg-primary text-primary-foreground shadow-xs"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}>
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>

          {/* Mobile Inline Brand Badge */}
          <Link
            className='flex items-center gap-2.5 rounded-lg border border-border bg-muted/60 px-3 py-1.5 shadow-xs transition-colors hover:border-primary/50 hover:bg-muted lg:hidden'
            to='/'
            aria-label='MetricMap home'>
            <img src='/uk-flag.svg' alt='' className='size-6 rounded-xs object-cover shadow-xs' />
            <span className='text-sm font-extrabold tracking-wider text-foreground uppercase'>{projectName}</span>
          </Link>
        </div>

        <div className='flex items-center gap-2'>
          <ShareLinkButton iconOnly />

          <ThemeToggle className='size-9 rounded-full border border-border bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground' />

          <button
            type='button'
            onClick={() => {
              setIsMenuOpen((open) => !open);
            }}
            className='flex size-9 items-center justify-center rounded-full border border-border bg-muted/60 text-foreground transition-colors hover:bg-muted lg:hidden'
            aria-label={isMenuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={isMenuOpen}
            aria-controls='mobile-nav-menu'>
            {isMenuOpen ? <X className='size-4' /> : <Menu className='size-4' />}
          </button>
        </div>

        <div
          id='mobile-nav-menu'
          className={cn(
            "absolute inset-x-3 top-full mt-2 rounded-2xl border border-border bg-card/95 p-2 shadow-2xl backdrop-blur-xl transition-[opacity,transform,visibility] duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)] lg:hidden",
            isMenuOpen || isNavExpanded
              ? "visible translate-y-0 scale-100 opacity-100"
              : "invisible -translate-y-2 scale-[0.98] opacity-0",
          )}>
          <div className='flex flex-col gap-1'>
            {navItems.map((item, index) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => {
                  setIsMenuOpen(false);
                }}
                style={{ transitionDelay: isMenuOpen ? `${50 + index * 30}ms` : "0ms" }}
                className={cn(
                  "rounded-xl px-3 py-2.5 text-sm font-medium transition-[opacity,transform,background-color,color] duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)]",
                  isMenuOpen ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0",
                  isActive(item)
                    ? "bg-primary font-semibold text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
